/**
 * SizeWindowInput — structured "Company size — global" field (E07-F02-T19 rework).
 *
 * Replaces the old free-text + datalist input (QA: arbitrary text broke the size
 * gate, and the "≤300" preset looked like it overlapped "50–500"/"11–500"). The user
 * picks ONE kind of bound (Any / Up to N / Between N and M / N or more) and types
 * only numbers; a plain-language line states exactly which companies pass. The
 * stored value keeps the API contract: "≤300" / "11–500" / "1000+" / '' (any).
 */
import { useState, type JSX } from 'react';
import {
  SIZE_WINDOW_MODES,
  describeSizeWindow,
  formatSizeWindow,
  sizeWindowError,
  sizeWindowParts,
  type SizeWindowMode,
  type SizeWindowParts,
} from './intakeModel.js';

export interface SizeWindowInputProps {
  /** The stored window ("≤300", "11–500", "1000+", '' = any). */
  readonly value: string;
  /** Fired with the composed value ('' while the window is incomplete). */
  readonly onChange: (value: string) => void;
}

const inputCls =
  'border border-[var(--color-border)] rounded-md p-2 text-sm bg-white outline-none focus:border-[var(--color-accent)] w-24';

const toNum = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw));

export function SizeWindowInput({ value, onChange }: SizeWindowInputProps): JSX.Element {
  const [parts, setParts] = useState<SizeWindowParts>(() => sizeWindowParts(value));
  const [seen, setSeen] = useState(value);
  // An outside change (Parse & autofill, clone) re-seeds the structured parts;
  // our own incomplete edits (value '' while typing) do not.
  if (value !== seen) {
    setSeen(value);
    if (value !== formatSizeWindow(parts)) setParts(sizeWindowParts(value));
  }

  const update = (next: SizeWindowParts): void => {
    setParts(next);
    const composed = formatSizeWindow(next);
    setSeen(composed);
    onChange(composed);
  };

  const error = sizeWindowError(parts);
  const showMin = parts.mode === 'range' || parts.mode === 'min';
  const showMax = parts.mode === 'range' || parts.mode === 'max';

  return (
    <div className="flex flex-col gap-1" data-testid="intake-size-global">
      <select
        data-testid="intake-size-global-mode"
        aria-label="Company size — global: kind of limit"
        className="border border-[var(--color-border)] rounded-md p-2 text-sm bg-white outline-none focus:border-[var(--color-accent)]"
        value={parts.mode}
        onChange={(e) => {
          const mode = e.target.value as SizeWindowMode;
          update({
            mode,
            min: mode === 'range' || mode === 'min' ? parts.min : null,
            max: mode === 'range' || mode === 'max' ? parts.max : null,
          });
        }}
      >
        {SIZE_WINDOW_MODES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      {showMin || showMax ? (
        <div className="flex items-center gap-2 text-sm">
          {showMin ? (
            <>
              <span>{parts.mode === 'range' ? 'from' : 'at least'}</span>
              <input
                type="number"
                min={0}
                step={1}
                data-testid="intake-size-global-min"
                aria-label="Minimum employees"
                className={inputCls}
                value={parts.min ?? ''}
                onChange={(e) => update({ ...parts, min: toNum(e.target.value) })}
              />
            </>
          ) : null}
          {showMax ? (
            <>
              <span>{parts.mode === 'range' ? 'to' : 'at most'}</span>
              <input
                type="number"
                min={1}
                step={1}
                data-testid="intake-size-global-max"
                aria-label="Maximum employees"
                className={inputCls}
                value={parts.max ?? ''}
                onChange={(e) => update({ ...parts, max: toNum(e.target.value) })}
              />
            </>
          ) : null}
          <span>employees</span>
        </div>
      ) : null}
      {error ? (
        <span
          data-testid="intake-size-global-invalid"
          className="text-xs text-[var(--color-danger)]"
        >
          {error}
        </span>
      ) : (
        <span
          data-testid="intake-size-global-hint"
          className="text-xs text-[var(--color-text-muted)]"
        >
          {describeSizeWindow(value)}
        </span>
      )}
    </div>
  );
}
